import { renderField } from "@CommonLib/src/frontend/WizardForm/RenderField";
import {
	type FormValues,
	type LocalStateStorageSchema,
	localStateStorageSchema,
	type OnFileUploadPayload,
	type OnKeystrokePayload,
	type OnNextPayload,
	type OnSelectChoicePayload,
	type StepI,
	type SubmitPayloadSchema,
	type WizardFormConfig,
	type WizardFormProps,
	type WizardFormRef,
} from "@CommonLib/src/frontend/WizardForm/types";
import {
	countAnsweredFields,
	countTotalFieldsInPath,
	fieldsFromStepId,
	filterOutFileFieldValues,
	findChildren,
	getFieldFromId,
	getFieldSource,
	getRootStep,
	lintWizardFormConfig,
} from "@CommonLib/src/frontend/WizardForm/utils";
import { isJsonParsable } from "@CommonLib/src/utils/strings";
import {
	Box,
	Button,
	Card,
	CardContent,
	LinearProgress,
	Stack,
	Toolbar,
	Typography,
} from "@mui/material";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

export const LOCAL_STORAGE_KEY = "wizard-form-state";

const saveLocalState = (state: LocalStateStorageSchema) => {
	const stateValidated = localStateStorageSchema.parse(state);
	localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateValidated));
};

const isLastStep = (config: WizardFormConfig, stepId: number) => {
	const children = findChildren(config.steps, stepId);
	return children.length === 0;
};

const findNextStepId = (
	config: WizardFormConfig,
	stepId: number,
	formValues: FormValues,
) => {
	const childCandidates = findChildren(config.steps, stepId);

	// if there is only 1 child, then it's the next step
	if (childCandidates.length === 1) return childCandidates[0]?.id;

	// We have multiple children
	for (const child of childCandidates) {
		const triggerChoice = child.triggerChoice;
		if (!triggerChoice) continue;
		const stepId = triggerChoice.stepId;
		const fieldId = triggerChoice.fieldId;
		const field = getFieldFromId(config, stepId, fieldId);
		const fieldKey = getFieldSource(stepId, field.identifier);
		const choiceId = triggerChoice.choiceId;
		if (formValues[fieldKey] === choiceId && field.type === "select")
			return child.id;
	}
	// If none of the children worked, we have an issue, because field on which there is a condition must be required
	// so if Next button was pressed it means all required fields were filled
	throw Error();
};

export const WizardForm = ({
	config,
	previewMode,
	sendData,
	formRef,
	onKeystroke,
	onNext,
	onFileUpload,
	onSelectChoice,
}: WizardFormProps) => {
	const rootStep = getRootStep(config.steps);
	if (!rootStep) return null;

	return (
		<WizardFormImplementation
			config={config}
			rootStep={rootStep}
			previewMode={previewMode}
			sendData={sendData}
			formRef={formRef}
			onKeystroke={onKeystroke}
			onNext={onNext}
			onFileUpload={onFileUpload}
			onSelectChoice={onSelectChoice}
		/>
	);
};

interface WizardFormImplementationProps {
	config: WizardFormConfig;
	previewMode: boolean;
	rootStep: StepI;
	sendData: (payload: SubmitPayloadSchema) => Promise<void>;
	formRef?: React.RefObject<WizardFormRef> | undefined;
	onKeystroke?: ((payload: OnKeystrokePayload) => void) | undefined;
	onNext?: ((payload: OnNextPayload) => void) | undefined;
	onFileUpload?: ((payload: OnFileUploadPayload) => void) | undefined;
	onSelectChoice?: ((payload: OnSelectChoicePayload) => void) | undefined;
}

export const WizardFormImplementation = (
	props: WizardFormImplementationProps,
) => {
	const {
		config,
		rootStep,
		previewMode,
		sendData,
		formRef,
		onKeystroke,
		onNext,
		onFileUpload,
		onSelectChoice,
	} = props;
	const formBoxRef = useRef<HTMLDivElement>(null);
	// We lint the form config first
	lintWizardFormConfig(config);

	const firstStepId = rootStep.id;

	const methods = useForm({ mode: "onChange" });

	const [stepHistory, setStepHistory] = useState<number[]>([firstStepId]);
	const [stepId, setStepId] = useState<number>(firstStepId);
	const [currentFieldIndex, setCurrentFieldIndex] = useState<number>(0);

	const [fieldKeystrokes, setFieldKeystrokes] = useState<
		Record<string, { first: number; last: number }>
	>({});

	const handleFieldKeystroke = (fieldKey: string) => {
		setFieldKeystrokes((prev) => {
			const now = Date.now();
			const existing = prev[fieldKey];
			if (existing) {
				return { ...prev, [fieldKey]: { ...existing, last: now } };
			} else {
				return { ...prev, [fieldKey]: { first: now, last: now } };
			}
		});
	};

	const onKeystrokeRef = useRef(onKeystroke);
	onKeystrokeRef.current = onKeystroke;

	useEffect(() => {
		const box = formBoxRef.current;
		if (!box || !onKeystrokeRef.current) return;

		const handler = (e: KeyboardEvent) => {
			if (!onKeystrokeRef.current) return;
			// BE expects key to be there for keystroke events
			if (!e.key) return;
			const active = document.activeElement;
			let fieldKey: string | null = null;
			if (active && box.contains(active)) {
				const name = active.getAttribute("name");
				if (name) {
					fieldKey = name;
				} else {
					const dataKey = active.getAttribute("data-field-key");
					if (dataKey) fieldKey = dataKey;
				}
			}
			onKeystrokeRef.current({ key: e.key, fieldKey, timestamp: Date.now() });
		};

		box.addEventListener("keydown", handler);
		return () => box.removeEventListener("keydown", handler);
	}, []);

	useEffect(() => {
		// In preview mode, always start fresh — clear any saved state and reset form
		if (previewMode) {
			localStorage.removeItem(LOCAL_STORAGE_KEY);
			setStepId(firstStepId);
			setStepHistory([firstStepId]);
			setCurrentFieldIndex(0);
			methods.reset({});
			setFieldKeystrokes({});
			return;
		}
		const saved = localStorage.getItem(LOCAL_STORAGE_KEY) ?? "";
		let loaded = false;
		// We only restore local state if it is readable
		if (isJsonParsable(saved)) {
			const result = localStateStorageSchema.safeParse(JSON.parse(saved));
			if (result.success) {
				const state = result.data;
				const {
					values,
					stepId: savedStepId,
					configId,
					keystrokes,
					currentFieldIndex: savedFieldIndex,
				} = state;
				// we only restore local state if configId matches (else form versions are different, could cause issues)
				if (configId === config.configId) {
					loaded = true;
					setStepHistory([savedStepId]);
					setStepId(savedStepId);
					setCurrentFieldIndex(savedFieldIndex);
					methods.reset(values);
					setFieldKeystrokes(keystrokes);
				}
			}
		}
		// if localState is not readable, we delete it
		if (loaded === false) {
			localStorage.removeItem(LOCAL_STORAGE_KEY);
		}
	}, [methods, config.configId, previewMode]);

	// Save to localStorage on any form or step change
	const formValues = useWatch({ control: methods.control });
	useEffect(() => {
		// Don't persist state in preview mode
		if (previewMode) return;
		// We only save something if there is something to save
		// Necessary to ease unit test that ensures localState is deleted if corrupted
		if (Object.keys(formValues).length === 0) return;
		// Filter out file field values before saving to localStorage
		const filteredValues = filterOutFileFieldValues(config, formValues);
		saveLocalState({
			values: filteredValues,
			stepId: stepId,
			configId: config.configId,
			currentFieldIndex: currentFieldIndex,
			keystrokes: fieldKeystrokes,
		});
	}, [
		formValues,
		stepId,
		config.configId,
		currentFieldIndex,
		fieldKeystrokes,
		config,
		previewMode,
	]);

	const next = async () => {
		const fields = fieldsFromStepId(config, stepId);
		const currentField = fields[currentFieldIndex];
		if (!currentField) return;

		const currentFieldKey = getFieldSource(stepId, currentField.identifier);
		const valid = await methods.trigger(currentFieldKey);
		if (!valid) return;

		onNext?.({ fromStepId: stepId, fromFieldId: currentField.identifier });

		const isLastField = currentFieldIndex >= fields.length - 1;
		if (!isLastField) {
			setCurrentFieldIndex((prev) => prev + 1);
			return;
		}

		const nextStepId = findNextStepId(config, stepId, formValues);
		// If already at last defined step, do nothing
		if (nextStepId === undefined) return;

		setStepHistory((prev) => [...prev, nextStepId]);
		setStepId(nextStepId);
		setCurrentFieldIndex(0);
	};

	// biome-ignore lint/suspicious/noExplicitAny: only here
	const submitFunction = async (data: any) => {
		const payload: SubmitPayloadSchema = {
			responses: data,
			configId: config.configId,
			keystrokes: fieldKeystrokes,
		};
		await sendData(payload);
		localStorage.removeItem(LOCAL_STORAGE_KEY);
	};

	const handleFinalSubmit = methods.handleSubmit(async (data) => {
		await submitFunction(data);
	});

	// This exposes submit method to parent components
	// submitForm bypasses validation so forced submits (fullscreen exit, keyboard timeout)
	// always send whatever data the user has entered
	useImperativeHandle(formRef, () => ({
		submitForm: async () => {
			const data = methods.getValues();
			await submitFunction(data);
		},
	}));

	const totalFields = countTotalFieldsInPath(
		config,
		stepHistory,
		formValues,
		stepId,
	);
	const answeredFields = countAnsweredFields(
		config,
		stepHistory,
		currentFieldIndex,
	);
	const progressPercent =
		totalFields > 0 ? (answeredFields / totalFields) * 100 : 0;

	return (
		<FormProvider {...methods}>
			<Box ref={formBoxRef}>
				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" sx={{ mb: 0.5 }}>
						Question {answeredFields + 1} of {totalFields}
					</Typography>
					<LinearProgress variant="determinate" value={progressPercent} />
				</Box>

				<Card sx={{ maxWidth: 720, mx: "auto", marginBottom: "10px" }}>
					<CardContent>
						{/* key={stepId}-${currentFieldIndex} forces rerender when step or field changes */}
						<Stack key={`${stepId}-${currentFieldIndex}`} spacing={2}>
							{(() => {
								const fields = fieldsFromStepId(config, stepId);
								const field = fields[currentFieldIndex];
								if (!field) return null;
								const handleFileUpload = onFileUpload
									? (fieldKey: string) =>
											onFileUpload({
												fieldKey,
												timestamp: Date.now(),
											})
									: undefined;
								const handleSelectChoice = onSelectChoice
									? (fieldKey: string) =>
											onSelectChoice({
												fieldKey,
												timestamp: Date.now(),
											})
									: undefined;
								return renderField(
									stepId,
									field,
									handleFieldKeystroke,
									methods.control,
									methods.formState.errors,
									handleFileUpload,
									handleSelectChoice,
								);
							})()}
						</Stack>

						<Toolbar sx={{ justifyContent: "flex-end", mt: 2, px: 0 }}>
							{isLastStep(config, stepId) &&
							currentFieldIndex >=
								fieldsFromStepId(config, stepId).length - 1 ? (
								<Button
									variant="contained"
									color="primary"
									onClick={handleFinalSubmit}
									disabled={previewMode}
								>
									Save
								</Button>
							) : (
								<Button variant="contained" onClick={next}>
									Next
								</Button>
							)}
						</Toolbar>
					</CardContent>
				</Card>
			</Box>
		</FormProvider>
	);
};
